import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Avatar, Box, Button, Card, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, IconButton, Stack, Switch, TextField, Tooltip, Typography, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper, Chip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { fetchAllBoards, createBoard, deleteBoard, updateBoard, fetchBoardById, reorderBoards } from '../../controllers/boardsController';
import { fetchBoardInfo, updateBoardInfo } from '../../controllers/boardInfoController';
import { searchMembers } from '../../controllers/memberManagementController';
import { resolveGroupsAssetUrl } from '../../api/httpClientGroups';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const BoardsList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ title: '', isActive: true });
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', isActive: true });
  const [updating, setUpdating] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [memberQuery, setMemberQuery] = useState('');
  const [memberOptions, setMemberOptions] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);

  // Board Info (genel bilgi: dönem, başlık, açıklama)
  const [boardInfo, setBoardInfo] = useState({ term: '', title: '', description: '' });
  const [boardInfoLoading, setBoardInfoLoading] = useState(false);
  const [boardInfoSaving, setBoardInfoSaving] = useState(false);
  const [boardInfoError, setBoardInfoError] = useState('');
  const [boardInfoEditing, setBoardInfoEditing] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAllBoards();
      setItems(data || []);
    } catch (e) {
      setError(e?.message || 'Kurullar alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      setBoardInfoLoading(true);
      setBoardInfoError('');
      try {
        const info = await fetchBoardInfo();
        if (!active) return;
        if (info) {
          setBoardInfo({
            term: info.term || '',
            title: info.title || '',
            description: info.description || '',
          });
        }
      } catch (e) {
        if (!active) return;
        setBoardInfoError(e?.message || 'Kurul bilgisi alınamadı');
      } finally {
        if (active) setBoardInfoLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} - ${hours}:${minutes}`;
  };

  const handleBoardInfoChange = (field) => (e) => {
    const value = e.target.value;
    setBoardInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleBoardInfoSave = async () => {
    if (!boardInfo.term || !boardInfo.term.trim()) return;
    setBoardInfoSaving(true);
    setBoardInfoError('');
    try {
      await updateBoardInfo({
        term: boardInfo.term.trim(),
        title: boardInfo.title?.trim() || null,
        description: boardInfo.description?.trim() || null,
        coverImageUrl: null,
      });
    } catch (e) {
      setBoardInfoError(e?.message || 'Kurul bilgisi güncellenemedi');
    } finally {
      setBoardInfoSaving(false);
    }
  };

  const ids = useMemo(() => (items || []).map((b) => b.id), [items]);

  const SortableRow = ({ id, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };
    return children({ attributes, listeners, setNodeRef, style });
  };

  const handleEditOpen = async (board) => {
    setEditOpen(true);
    setEditLoading(true);
    try {
      const data = await fetchBoardById(board.id);
      setEditData(data);
      setEditForm({
        title: data.title || '',
        isActive: data.isActive !== undefined ? data.isActive : true,
        members: data.members || [],
      });
    } catch (e) {
      console.error('Kurul alınamadı:', e);
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setEditData(null);
    setEditForm({ title: '', isActive: true, members: [] });
    setSelectedMembers([]);
    setMemberQuery('');
    setMemberOptions([]);
  };

  useEffect(() => {
    let active = true;
    (async () => {
      if (!editOpen) return;
      if (!memberQuery || memberQuery.length < 2) {
        setMemberOptions([]);
        return;
      }
      try {
        const data = await searchMembers(memberQuery);
        if (!active) return;
        setMemberOptions(data || []);
      } catch (e) {
        if (!active) return;
        setMemberOptions([]);
      }
    })();
    return () => {
      active = false;
    };
  }, [editOpen, memberQuery]);

  if (loading) {
    return (
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Skeleton width={150} height={28} />
          <Skeleton width={120} height={36} />
        </Stack>
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
            border: '1px solid #f5f5f5',
            py: 1,
            px: 2.5
          }}
        >
          <Table size="small">
            <TableHead sx={{ bgcolor: 'transparent' }}>
              <TableRow>
                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3', width: 80 }}>Sıralama</TableCell>
                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Başlık</TableCell>
                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Kişiler</TableCell>
                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Durum</TableCell>
                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Oluşturulma Tarihi</TableCell>
                <TableCell align="right" sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 5 }).map((_, index) => (
                <TableRow
                  key={`skeleton-${index}`}
                  sx={{
                    '& .MuiTableCell-root': {
                      borderBottom: index === 4 ? 'none' : '1px dashed #e8e8e8',
                      px: 1.5,
                      py: 1.5,
                    },
                  }}
                >
                                    <TableCell>
                                      <Skeleton width={24} height={24} borderRadius={4} />
                                    </TableCell>
                                    <TableCell>
                                      <Skeleton width={200} height={20} />
                                    </TableCell>
                                    <TableCell>
                                      <Skeleton width={150} height={32} />
                                    </TableCell>
                                    <TableCell>
                                      <Skeleton width={60} height={24} borderRadius={16} />
                                    </TableCell>
                                    <TableCell>
                                      <Skeleton width={140} height={20} />
                                    </TableCell>
                                    <TableCell align="right">
                                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                        <Skeleton width={32} height={32} borderRadius={6} />
                                        <Skeleton width={32} height={32} borderRadius={6} />
                                      </Stack>
                                    </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }
  
  if (error) return <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>;

  return (
    <Box>
      {/* Kurul Genel Bilgisi (Dönem, Başlık) */}
      <Card
        sx={{
          mb: 3,
          border: '1px solid #e7e7e7',
          boxShadow: 'none',
          borderRadius: '8px',
          background: 'linear-gradient(90deg, #F6F8FB 0%, #FFFFFF 100%)',
        }}
      >
        <DialogContent sx={{ py: 2, px: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" sx={{ mb: 2, gap: 1.5 }}>
            <Box>
              <Typography sx={{ fontSize: 13, color: '#a3a3a3', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Yönetim Kurulu Bilgisi
              </Typography>
              {!boardInfoEditing && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 0.75 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Typography sx={{ fontSize: 12, color: '#a3a3a3' }}>Dönem:</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#444444' }}>
                      {boardInfo.term || '-'}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Typography sx={{ fontSize: 12, color: '#a3a3a3' }}>Başlık:</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#444444' }} noWrap>
                      {boardInfo.title || '-'}
                    </Typography>
                  </Stack>
                </Stack>
              )}
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              {boardInfoLoading && <CircularProgress size={16} />}
              {!boardInfoEditing && !boardInfoLoading && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setBoardInfoEditing(true)}
                  sx={{
                    textTransform: 'none',
                    fontSize: 13,
                    borderRadius: '16px',
                    borderColor: '#d0d7e2',
                    color: '#0E4D94',
                    '&:hover': {
                      borderColor: '#0E4D94',
                      backgroundColor: '#E7EDF4',
                    },
                  }}
                >
                  Düzenle
                </Button>
              )}
            </Stack>
          </Stack>
          {boardInfoError && (
            <Typography color="error" sx={{ fontSize: 13, mb: 1 }}>
              {boardInfoError}
            </Typography>
          )}
          {boardInfoEditing && (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                    Dönem
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    required
                    value={boardInfo.term}
                    onChange={handleBoardInfoChange('term')}
                    placeholder="Örn: 2024-25"
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                    Başlık
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={boardInfo.title}
                    onChange={handleBoardInfoChange('title')}
                    placeholder="Opsiyonel başlık"
                  />
                </Box>
              </Stack>
              <Stack direction="row" justifyContent="flex-end" spacing={1}>
                <Button
                  variant="text"
                  size="small"
                  onClick={async () => {
                    setBoardInfoEditing(false);
                    setBoardInfoLoading(true);
                    setBoardInfoError('');
                    try {
                      const info = await fetchBoardInfo();
                      if (info) {
                        setBoardInfo({
                          term: info.term || '',
                          title: info.title || '',
                          description: info.description || '',
                        });
                      }
                    } catch (e) {
                      setBoardInfoError(e?.message || 'Kurul bilgisi alınamadı');
                    } finally {
                      setBoardInfoLoading(false);
                    }
                  }}
                  sx={{
                    textTransform: 'none',
                    fontSize: 13,
                  }}
                >
                  İptal
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={async () => {
                    await handleBoardInfoSave();
                    setBoardInfoEditing(false);
                  }}
                  disabled={boardInfoSaving || !boardInfo.term?.trim()}
                  sx={{
                    textTransform: 'none',
                    fontSize: 13,
                    borderRadius: '5px',
                  }}
                >
                  {boardInfoSaving ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
              </Stack>
            </Stack>
          )}
        </DialogContent>
      </Card>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Yönetim Kurulu</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ width: 16 }} />}
          sx={{
            textTransform: 'none',
            backgroundColor: '#E7EDF4',
            boxShadow: 'none',
            fontSize: 13,
            borderRadius: '5px',
            color: '#0E4D94',
            '&:hover': {
              backgroundColor: '#CFDBEA',
              boxShadow: 'none',
            },
          }}
          onClick={() => setCreateOpen(true)}
        >
          Yeni Kurul
        </Button>
      </Stack>

      {error ? (
        <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>
      ) : (
      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={(event) => {
          const { active, over } = event;
          if (!over || active.id === over.id) return;
          const oldIndex = ids.indexOf(active.id);
          const newIndex = ids.indexOf(over.id);
          setItems((prev) => {
            const newArr = arrayMove(prev, oldIndex, newIndex);
              // optimistic order update
            const updated = newArr.map((b, idx) => ({ ...b, order: idx }));
              // fire reorder API in background
            (async () => {
              try {
                await reorderBoards({ boardIdsInOrder: updated.map((b) => b.id) });
              } catch (e) {
                  // if fails, reload server order
                  try { await load(); } catch (_) { }
              }
            })();
            return updated;
          });
        }}
      >
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
                border: '1px solid #f5f5f5',
                py: 1,
                px: 2.5
              }}
            >
              <Table size="small">
                <TableHead sx={{ bgcolor: 'transparent' }}>
                  <TableRow>
                    <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3', width: 80 }}>Sıralama</TableCell>
                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Başlık</TableCell>
                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Kişiler</TableCell>
                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Durum</TableCell>
                <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Oluşturulma Tarihi</TableCell>
                <TableCell align="right" sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>İşlemler</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography sx={{ color: '#a3a3a3' }}>Henüz kurul eklenmemiş</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((b, index) => {
                      const members = b.members || [];
                      const maxDisplay = 8;
                      const displayMembers = members.slice(0, maxDisplay);
                      const remainingCount = members.length - maxDisplay;

                      return (
                        <SortableRow key={b.id} id={b.id}>
                          {({ attributes, listeners, setNodeRef, style }) => (
                            <TableRow
                              ref={setNodeRef}
                              style={style}
                              {...attributes}
                              hover
                              sx={{
                                cursor: 'move',
                                '& .MuiTableCell-root': {
                                  borderBottom: index === items.length - 1 ? 'none' : '1px dashed #e8e8e8',
                                  px: 1.5,
                                  py: 1.5,
                                  color: '#444444',
                                },
                              }}
                            >
                              <TableCell 
                                {...listeners}
                                sx={{ cursor: 'grab', userSelect: 'none' }}
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                <DragIndicatorIcon sx={{ color: '#a3a3a3', fontSize: 20 }} />
                              </TableCell>
                              <TableCell 
                                {...listeners}
                                sx={{ fontWeight: 500, cursor: 'grab', userSelect: 'none' }}
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                {b.title}
                              </TableCell>
                              <TableCell 
                                {...listeners}
                                sx={{ cursor: 'grab', userSelect: 'none' }}
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                {members.length > 0 ? (
                                  <Stack direction="row" spacing={-1.5} alignItems="center">
                                    {displayMembers.map((m, idx) => (
                                      <Avatar
                                        key={m.id}
                                        src={m.profilePictureUrl ? resolveGroupsAssetUrl(m.profilePictureUrl) : undefined}
                                        alt={m.fullName}
                                        sx={{
                                          width: 32,
                                          height: 32,
                                          border: '2px solid #ffffff',
                                          cursor: 'pointer',
                                          zIndex: displayMembers.length - idx,
                                          backgroundColor: m.profilePictureUrl ? 'transparent' : '#E7EDF4',
                                          '&:hover': {
                                            zIndex: 10,
                                            transform: 'scale(1.1)',
                                          },
                                        }}
                                      >
                                        {!m.profilePictureUrl && (
                                          <Typography sx={{ fontSize: '12px', color: '#0E4D94', fontWeight: 600 }}>
                                            {m.fullName?.charAt(0) || '?'}
                              </Typography>
                                        )}
                                      </Avatar>
                                    ))}
                                    {remainingCount > 0 && (
                                      <Avatar
                                        sx={{
                                          width: 32,
                                          height: 32,
                                          border: '2px solid #ffffff',
                                          backgroundColor: '#E7EDF4',
                                          color: '#0E4D94',
                                          fontSize: '11px',
                                          fontWeight: 600,
                                          cursor: 'pointer',
                                        }}
                                      >
                                        +{remainingCount}
                                      </Avatar>
                                    )}
                                  </Stack>
                                ) : (
                                  <Typography sx={{ color: '#a3a3a3', fontSize: '13px' }}>-</Typography>
                                )}
                              </TableCell>
                              <TableCell 
                                {...listeners}
                                sx={{ cursor: 'grab', userSelect: 'none' }}
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                {b.isActive ? (
                                  <Chip
                                    label="Aktif"
                                    size="small"
                                    sx={{
                                      height: 24,
                                      fontSize: '11px',
                                      fontWeight: 600,
                                      backgroundColor: '#DFFFEA',
                                      color: '#17C653',
                                      borderRadius: '12px',
                                    }}
                                  />
                                ) : (
                                  <Chip
                                    label="Pasif"
                                    size="small"
                                    sx={{
                                      height: 24,
                                      fontSize: '11px',
                                      fontWeight: 600,
                                      backgroundColor: '#FFF4E6',
                                      color: '#FF9800',
                                      borderRadius: '12px',
                                    }}
                                  />
                                )}
                              </TableCell>
                              <TableCell 
                                {...listeners}
                                sx={{ cursor: 'grab', userSelect: 'none' }}
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                {formatDate(b.createdAtUtc)}
                              </TableCell>
                              <TableCell 
                                align="right"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                  <Tooltip title="Düzenle">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditOpen(b);
                                      }}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      sx={{
                                        backgroundColor: '#E7EDF4',
                                        width: 32,
                                        height: 32,
                                        borderRadius: '6px',
                                        '&:hover': {
                                          backgroundColor: '#CFDBEA',
                                        },
                                        '& svg': {
                                          fontSize: '16px',
                                          color: '#0E4D94',
                                        },
                                      }}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </Tooltip>
                        <Tooltip title="Sil">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelected(b);
                                        setConfirmOpen(true);
                                      }}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      sx={{
                                        backgroundColor: '#FFEEF3',
                                        width: 32,
                                        height: 32,
                                        borderRadius: '6px',
                                        '&:hover': {
                                          backgroundColor: '#FFD6E0',
                                        },
                                        '& svg': {
                                          fontSize: '16px',
                                          color: '#F8285A',
                                        },
                                      }}
                                    >
                                      <DeleteOutlineIcon />
                          </IconButton>
                        </Tooltip>
                                </Stack>
                              </TableCell>
                            </TableRow>
                  )}
                        </SortableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
        </SortableContext>
      </DndContext>
      )}

      {/* Delete confirm */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Kurulu Sil
          <IconButton
            onClick={() => setConfirmOpen(false)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <DialogContentText sx={{ fontSize: '14px', color: '#666666' }}>
            {selected ? `"${selected.title}" kaydını silmek istediğinize emin misiniz?` : 'Seçili kaydı silmek istediğinize emin misiniz?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!selected) return;
              try {
                await deleteBoard(selected.id);
                setConfirmOpen(false);
                setSelected(null);
                await load();
              } catch (e) { }
            }}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#F8285A',
              borderRadius: '5px',
              px: 2,
              '&:hover': {
                backgroundColor: '#E01E4A',
              },
            }}
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Yeni Kurul
          <IconButton
            onClick={() => setCreateOpen(false)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Başlık
              </Typography>
              <TextField
                fullWidth
                value={createForm.title}
                onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                placeholder=""
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={() => {
              setCreateOpen(false);
              setCreateForm({ title: '', isActive: true });
            }}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                await createBoard({ title: createForm.title, members: [], isActive: true });
                setCreateOpen(false);
                setCreateForm({ title: '', isActive: true });
                await load();
              } catch (e) { }
            }}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#0E4D94',
              borderRadius: '5px',
              px: 2,
              py: 0.7,
              '&:hover': {
                backgroundColor: '#0a3d7a',
              },
            }}
          >
            Oluştur
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onClose={handleEditClose} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Kurulu Düzenle
          <IconButton
            onClick={handleEditClose}
            disabled={updating}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          {editLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : editData ? (
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Kurul Adı
                </Typography>
                <TextField
                  fullWidth
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  disabled={updating}
                  placeholder=""
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Durum
                </Typography>
                <FormControlLabel
                  sx={{
                    m: 0,
                    ml: 0,
                    mr: 0,
                    pl: 0,
                    '& .MuiFormControlLabel-label': { ml: 1 },
                  }}
                  control={
                    <Switch
                      checked={!!editForm.isActive}
                      onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                      disabled={updating}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#0E4D94' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#0E4D94' },
                      }}
                    />
                  }
                  label={editForm.isActive ? 'Aktif' : 'Pasif'}
                />
              </Box>

              <Box>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Üye Ekle
                </Typography>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Autocomplete
                    multiple
                    sx={{
                      flex: 1,
                    }}
                    options={memberOptions}
                    getOptionLabel={(o) => o.fullName || ''}
                    value={selectedMembers}
                    onChange={(e, v) => setSelectedMembers(v || [])}
                    onInputChange={(e, v) => setMemberQuery(v)}
                    loading={false}
                    size="small"
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionDisabled={(option) => {
                      // Disable already added members
                      return (editForm.members || []).some((m) => m.id === option.id);
                    }}
                    renderOption={(props, option) => {
                      const isDisabled = (editForm.members || []).some((m) => m.id === option.id);
                      return (
                        <Box
                          component="li"
                          {...props}
                          key={option.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            opacity: isDisabled ? 0.5 : 1,
                          }}
                        >
                          <Avatar src={resolveGroupsAssetUrl(option.profilePictureUrl)} sx={{ width: 28, height: 28 }} />
                          {option.fullName}
                          {isDisabled && <Typography variant="caption" sx={{ ml: 'auto', color: '#a3a3a3' }}>(Zaten ekli)</Typography>}
                        </Box>
                      );
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          label={option.fullName}
                          avatar={<Avatar src={resolveGroupsAssetUrl(option.profilePictureUrl)} sx={{ width: 20, height: 20 }} />}
                          size="small"
                          sx={{
                            backgroundColor: '#E7EDF4',
                            color: '#0E4D94',
                            fontSize: '11px',
                            height: '24px',
                            '& .MuiChip-deleteIcon': {
                              fontSize: '14px',
                              color: '#0E4D94',
                              '&:hover': {
                                color: '#0a3d7a',
                              },
                            },
                          }}
                        />
                      ))
                    }
                    slotProps={{ paper: { sx: { width: 560 } }, listbox: { sx: { maxHeight: 360 } } }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={selectedMembers.length > 0 ? `${selectedMembers.length} üye seçildi` : 'Üye ara ve ekle...'}
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '5px',
                            backgroundColor: '#fafafa',
                            minHeight: '36px',
                            '&:hover': {
                              backgroundColor: '#ffffff',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#0E4D94',
                              },
                            },
                            '&.Mui-focused': {
                              backgroundColor: '#ffffff',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#0E4D94',
                                borderWidth: '1px',
                              },
                            },
                          },
                          '& .MuiAutocomplete-inputRoot': {
                            py: selectedMembers.length > 0 ? '2px' : '4px',
                            px: '10px',
                          },
                          '& .MuiAutocomplete-input': {
                            py: 0,
                            px: 0,
                            fontSize: '13px',
                          },
                          '& .MuiInputBase-input': {
                            py: 0.8,
                            px: 1,
                            fontSize: '13px',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e6e6e6',
                            borderWidth: '1px',
                          },
                        }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <IconButton
                    onClick={async () => {
                      if (selectedMembers.length === 0 || !editData) return;
                      setAddingMember(true);
                      try {
                        const existingIds = (editForm.members || []).map((m) => m.id);
                        const newMembersToAdd = selectedMembers
                          .filter((m) => !existingIds.includes(m.id))
                          .map((m) => ({
                            id: m.id,
                            fullName: m.fullName || '',
                            profilePictureUrl: m.profilePictureUrl || null,
                          }));
                        if (newMembersToAdd.length === 0) {
                          setSelectedMembers([]);
                          return;
                        }
                        const newMembers = [...(editForm.members || []), ...newMembersToAdd];
                        await updateBoard({ id: editData.id, title: editForm.title, members: newMembers, isActive: editForm.isActive });
                        setEditForm((p) => ({ ...p, members: newMembers }));
                        setSelectedMembers([]);
                        await load();
                      } catch (e) {
                        // noop
                      } finally {
                        setAddingMember(false);
                      }
                    }}
                    disabled={selectedMembers.length === 0 || addingMember || updating}
                    sx={{
                      backgroundColor: selectedMembers.length > 0 && !addingMember && !updating ? '#0E4D94' : '#e6e6e6',
                      color: selectedMembers.length > 0 && !addingMember && !updating ? '#ffffff' : '#a3a3a3',
                      borderRadius: '5px',
                      width: 36,
                      height: 36,
                      mt: 0.5,
                      '&:hover': {
                        backgroundColor: selectedMembers.length > 0 && !addingMember && !updating ? '#0a3d7a' : '#e6e6e6',
                      },
                      '&:disabled': {
                        backgroundColor: '#e6e6e6',
                        color: '#a3a3a3',
                      },
                    }}
                  >
                    {addingMember ? <CircularProgress size={16} color="inherit" /> : <AddIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </Stack>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Üyeler
                </Typography>
                {(editForm.members || []).length === 0 ? (
                  <Typography sx={{ color: '#a3a3a3', fontSize: 13 }}>Henüz üye eklenmemiş</Typography>
                ) : (
                  <Stack spacing={1}>
                    {(editForm.members || []).map((m) => (
                      <Box
                        key={m.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          border: '1px dashed #e8e8e8',
                          borderRadius: '8px',
                          px: 1.25,
                          py: 0.9,
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                          <Avatar
                            src={m.profilePictureUrl ? resolveGroupsAssetUrl(m.profilePictureUrl) : undefined}
                            alt={m.fullName}
                            sx={{
                              width: 34,
                              height: 34,
                              backgroundColor: m.profilePictureUrl ? 'transparent' : '#E7EDF4',
                            }}
                          >
                            {!m.profilePictureUrl && (
                              <Typography sx={{ fontSize: 13, color: '#0E4D94', fontWeight: 600 }}>
                                {m.fullName?.charAt(0) || '?'}
                              </Typography>
                            )}
                          </Avatar>
                          <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#444444', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {m.fullName}
                          </Typography>
                        </Stack>
                        <Tooltip title="Kaldır">
                          <IconButton
                            size="small"
                            disabled={updating || addingMember}
                            onClick={async () => {
                              if (!editData) return;
                              const newMembers = (editForm.members || []).filter((x) => x.id !== m.id);
                              try {
                                await updateBoard({ id: editData.id, title: editForm.title, members: newMembers, isActive: editForm.isActive });
                                setEditForm((p) => ({ ...p, members: newMembers }));
                                await load();
                              } catch (e) {
                                // noop
                              }
                            }}
                            sx={{
                              backgroundColor: '#FFEEF3',
                              width: 32,
                              height: 32,
                              borderRadius: '6px',
                              '&:hover': {
                                backgroundColor: '#FFD6E0',
                              },
                              '& svg': {
                                fontSize: '16px',
                                color: '#F8285A',
                              },
                            }}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={handleEditClose}
            disabled={updating}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!editData) return;
              setUpdating(true);
              try {
                await updateBoard({
                  id: editData.id,
                  title: editForm.title,
                  members: editForm.members || [],
                  isActive: editForm.isActive,
                });
                handleEditClose();
              await load();
              } catch (e) {
                console.error('Kurul güncellenemedi:', e);
              } finally {
                setUpdating(false);
              }
            }}
            disabled={updating}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#0E4D94',
              borderRadius: '5px',
              px: 2,
              py: 0.7,
              '&:hover': {
                backgroundColor: '#0a3d7a',
              },
            }}
          >
            {updating ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BoardsList;
